import { IconBottle, IconBox, IconBuildingStore, IconCarambola, IconCash, IconChartBar, IconCircleCheck, IconDiscount, IconFile, IconInvoice, IconReceiptDollar, IconSettings, IconSitemap, IconTruckDelivery, IconUsers, IconUsersGroup, IconWashMachine } from "@tabler/icons-react";
import { LinksGroupProps } from "../NavbarLinksGroup/NavbarLinksGroup";
import moment from "moment";
import { canViewFinancialSummary, hasPermission } from "../../utils/permission-utils";

export const buildMenuByPermission = (wdPermissions = {
  auth: { canView: false },
  company: { canView: false },
  product: { canView: false },
  section: { canView: false },
  staff: { canView: false },
  store: { canView: false },
  settings: { canView: false },
  cashierBoxes: { canView: false },
  reports: { canView: false },
  customers: { canView: false },
  discounts: { canView: false },
  reviews: { canView: false },
  inventory: { canView: false },
  expenses: { canView: false },
  cfdi: { canView: false }
}, company, t) => {
  const permissions = wdPermissions;
  const enableOutsourcing = company?.companyServicesPermissions?.enableOutsourcing;
  //THIS OPTIONS ARE ADDED ON MOBILE BECAUSE OF HEADER OPTIONS REMOVED ON MOBILE VIEW
  const menuOptions: LinksGroupProps[] = [
    {
      label: t('MenuOptions:newOrder'),
      link: '/',
      icon: IconReceiptDollar,
      hiddenFrom: 'sm'
    },
    {
      label: t('MenuOptions:clean'),
      link: '/cleaning',
      icon: IconWashMachine,
      hiddenFrom: 'sm'
    },
    {
      label: t('MenuOptions:ready'),
      link: '/ready',
      icon: IconCircleCheck,
      hiddenFrom: 'sm'
    },
    {
      label: t('MenuOptions:pickups'),
      link: '/pickup',
      icon: IconTruckDelivery,
      hiddenFrom: 'sm'
    },
    {
      label: t('MenuOptions:orders'),
      link: '/orders',
      icon: IconReceiptDollar
    }
  ];

  if (enableOutsourcing) {
    const outsourcingOption: LinksGroupProps = {
      label: t('MenuOptions:outsourcing'),
      link: '/outsourcing',
      icon: IconBox,
      hiddenFrom: 'sm'
    };

    menuOptions.splice(2, 0, outsourcingOption);
  }

  if (company?.companyServicesPermissions?.canUseDeliveryApp) {
    menuOptions.push({
      icon: IconTruckDelivery,
      link: '/routes',
      label: t('MenuOptions:routes')
    })
  }

  if (
    company?.companyServicesPermissions?.canUseCFDIInvoicing &&
    (permissions?.cfdi?.canView ?? true)
  ) {
    menuOptions.push({
      icon: IconInvoice,
      link: '/CFDI',
      label: t('MenuOptions:invoices')
    })
  }

  if (hasPermission({ permission: permissions }, 'customers', 'canView')) {
    menuOptions.push({
      icon: IconUsersGroup,
      link: '/customers',
      label: t('MenuOptions:customers')
    });
  }


  if (permissions.section?.canView || permissions.product?.canView) {
    const sectionsProductsLinks: LinksGroupProps = {
      icon: IconSitemap,
      label: t('MenuOptions:sectionsProducts'),
      links: [],
      link: '/sections',
      childLinksURL: ['/sections', '/products']
    }
    permissions.section?.canView && sectionsProductsLinks.links?.push({
      className: "tourSectionSideMenuSection",
      label: t('MenuOptions:sections'),
      link: '/sections'
    });
    permissions.product?.canView && sectionsProductsLinks.links?.push({
      className: "tourProductSideMenuSection",
      label: t('MenuOptions:products'),
      link: '/products'
    });
    menuOptions.push(sectionsProductsLinks);
  }

  if (permissions.inventory?.canView) {
    const inventorySuppliesLinks: LinksGroupProps = {
      icon: IconBottle,
      label: t('MenuOptions:inventoySupplies'),
      links: [],
      link: '/supply',
      childLinksURL: ['/supply', '/inventory']
    }
    inventorySuppliesLinks.links?.push({
      label: t('MenuOptions:inventory'),
      link: '/inventory'
    });
    inventorySuppliesLinks.links?.push({
      label: t('MenuOptions:supplies'),
      link: '/supply'
    });
    menuOptions.push(inventorySuppliesLinks);
  }

  if (permissions.cashierBoxes?.canView) {
    menuOptions.push({
      icon: IconCash,
      link: '/cashierBoxes',
      label: t('MenuOptions:cashierBoxes')
    });
  }

  const canViewExpenses = hasPermission({ permission: permissions }, 'expenses', 'canView');
  const canCreateExpenses = hasPermission({ permission: permissions }, 'expenses', 'canCreate');

  if (canViewExpenses || canCreateExpenses) {
    menuOptions.push({
      icon: IconReceiptDollar,
      link: canViewExpenses ? '/expenses' : '/expenses/create',
      label: t('MenuOptions:expenses')
    });
  }

  if (permissions.staff?.canView) {
    menuOptions.push({
      icon: IconUsers,
      link: '/staff',
      label: t('MenuOptions:staff')
    });
  }

  if (permissions.reports?.canView) {
    const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
    const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
    const reportLinks: LinksGroupProps = {
      icon: IconChartBar,
      label: t('MenuOptions:reports'),
      links: [],
      link: '/reports'
    }
    // — General —
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.sections.general'),
      link: '',
      isHeader: true
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.generalView.menuTitle'),
      link: '/reports'
    });

    // — Finanzas —
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.sections.finance'),
      link: '',
      isHeader: true
    });
    if (canViewFinancialSummary({ permission: permissions })) {
      reportLinks.links?.push({
        label: t('MenuOptions:reports_page.financialSummary.menuTitle'),
        link: '/reports/financial-summary'
      });
    }
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.salesReport.menuTitle'),
      link: '/reports/sales-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.salesReportMonthly.menuTitle'),
      link: '/reports/sales-report-monthly'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.paymentLinesReport.menuTitle'),
      link: '/reports/payment-lines-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.cashierBoxMovementsReport.menuTitle'),
      link: '/reports/cashierbox-movements-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.cashupReportsReport.menuTitle'),
      link: `/reports/cashup-reports-report?fromDate=${startOfMonth}&toDate=${endOfMonth}`,
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.unpaidOrdersReport.menuTitle'),
      link: '/reports/unpaid-orders-report'
    });

    // — Operación —
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.sections.operations'),
      link: '',
      isHeader: true
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.ordersHistoryReport.menuTitle'),
      link: '/reports/orders-history-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.servicesToDeliverReport.title'),
      link: '/reports/services-to-deliver-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.scheduledDeliveriesReport.menuTitle'),
      link: '/reports/scheduled-deliveries-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.ordersCleaned.menuTitle'),
      link: '/reports/cleaned-report'
    });
    if (enableOutsourcing) {
      reportLinks.links?.push({
        label: t('MenuOptions:reports_page.outsourcingReport.menuTitle'),
        link: '/reports/outsourcing-report'
      });
    }
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.suppliesReport.menuTitle'),
      link: '/reports/supplies-report'
    });
    if (permissions.inventory?.canView === true) {
      reportLinks.links?.push({
        label: t('MenuOptions:reports_page.inventoryStockReport.menuTitle'),
        link: '/reports/inventory-stock'
      });
      reportLinks.links?.push({
        label: t('MenuOptions:reports_page.supplyStockReport.menuTitle'),
        link: '/reports/supply-stock'
      });
    }

    // — Productos & Ventas —
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.sections.productsSales'),
      link: '',
      isHeader: true
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.salesProductReport.menuTitle'),
      link: '/reports/product-sales-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.discountCodesReport.menuTitle'),
      link: '/reports/discountCodes-report'
    });

    // — Clientes & Personal —
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.sections.clientsStaff'),
      link: '',
      isHeader: true
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.customersReport.menuTitle'),
      link: '/reports/customers-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.staffReport.menuTitle'),
      link: '/reports/staff-report'
    });
    reportLinks.links?.push({
      label: t('MenuOptions:reports_page.attendanceReport.menuTitle'),
      link: '/reports/attendance-report'
    });
    menuOptions.push(reportLinks);
  }

  if (permissions.store?.canView) {
    menuOptions.push({
      icon: IconBuildingStore,
      link: '/stores',
      label: t('MenuOptions:stores')
    });
  }

  if (permissions.discounts?.canView) {
    const discountLinks: LinksGroupProps = {
      icon: IconDiscount,
      label: t('MenuOptions:discounts'),
      links: [],
      link: '/discounts'
    }
    discountLinks.links?.push({
      label: t('MenuOptions:automaticDiscounts'),
      link: '/discounts/automaticCodes'
    });
    discountLinks.links?.push({
      label: t('MenuOptions:discountCodes'),
      link: '/discounts/discountCodes'
    });
    menuOptions.push(discountLinks);
  }

  if (permissions.reviews?.canView) {
    menuOptions.push({
      icon: IconCarambola,
      link: '/reviews',
      label: t('MenuOptions:reviews')
    });
  }

  if (permissions.settings?.canView) {
    menuOptions.push({
      icon: IconSettings,
      link: '/settings/store-details',
      label: t('MenuOptions:settings')
    });
  }

  return menuOptions;
};
